// The GTM activation funnel: three ONCE-PER-USER milestones over the chat-driven
// (gradual) onboarding.
//
//   Symphony Start On boarding  → the user's first message in the conversation
//   Symphony Finish On boarding → their first workspace is created
//   Symphony Active User        → their first message AFTER that workspace exists
//
// These are marketing-funnel definitions and deliberately do NOT mirror the product's
// own onboarding state: the backend's `finishedOnboarding` can flip with zero workspaces
// (`hollowFinishNudge` in finish-onboarding-tool.ts), whereas here "finished" means
// exactly "reached first workspace". The phone/code screens are not part of this funnel
// at all.
//
// ## Why a persistent latch is REQUIRED, not an optimization
//
// The workspace milestone is a persisted ASSISTANT message, so it is replayed into
// `messages` on every conversation load, forever. A latch scoped to a mount (or a ref)
// would re-fire "Finish On boarding" on every page load and every tab — turning a
// once-per-user activation metric into a page-view counter. localStorage is what makes
// these idempotent across reloads.
//
// The cost of that choice, stated plainly: the latch is per-browser, so the same user on
// a second device/browser can emit a duplicate. Deduping for real needs a server-side
// per-account flag; that is a backend change, and until then the browser latch is the
// honest approximation — it removes the reload storm, not cross-device doubles.
//
// ## Who is in this funnel at all — the cohort gate
//
// An activation funnel only means anything for users whose journey STARTS after it ships.
// Ungated, the day this merges every existing customer would emit "Finish On boarding" on
// their next load — the app simply notices the workspace they already have — and "Active
// User" on their next message, with no "Start" ever. The funnel would read finish > start
// for weeks and the conversion rate would be nonsense.
//
// So a browser is stamped once, the first time the account's conversation list is known:
// an empty account is a genuinely new user and the funnel applies to them; anything else is
// someone who onboarded before this shipped, and NONE of the three milestones will ever
// fire for them. Written once and never overwritten — recomputing it live would flip a real
// new user to `existing` the moment they created their first conversation, switching their
// funnel off halfway through.
//
// It FAILS CLOSED: with no stamp yet — the conversation list hasn't loaded, or its fetch
// failed — nothing fires. A missing event beats one attributed to the wrong cohort. The
// flip side is that the stamp is load-bearing: if `stampFunnelCohort` ever stops being
// called, the funnel goes silent rather than noisy, which is why gtm.test.ts pins its call
// site.
//
// This gate covers the three ACTIVATION milestones only. The packages/checkout events go
// through `pushGtmEvent` directly and are unaffected — an existing customer buying credits
// is exactly what those are for.

import { GtmEvent, type GtmEventName, pushGtmEvent } from './gtm';

type Milestone = 'start' | 'finish' | 'active';

const MILESTONES: Milestone[] = ['start', 'finish', 'active'];

type Cohort = 'new' | 'existing';

const COHORT_KEY = 'symphony-gtm-funnel:cohort';

const readCohort = (): Cohort | null => {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(COHORT_KEY);
    return raw === 'new' || raw === 'existing' ? raw : null;
  } catch {
    return null;
  }
};

// Record which cohort this browser belongs to, the first time the account's conversation
// list is known. `accountIsEmpty` means "this user has no conversations at all" — the one
// signal available on the client that separates a first-ever visit from a returning
// customer. Idempotent: the first answer wins forever.
export const stampFunnelCohort = (accountIsEmpty: boolean): void => {
  if (typeof window === 'undefined') return;
  if (readCohort()) return;
  try {
    window.localStorage.setItem(COHORT_KEY, accountIsEmpty ? 'new' : 'existing');
  } catch {
    // Unwritable storage — the browser stays unstamped and the funnel stays silent for it.
  }
};

// Whether this browser's user is one the funnel is about. Also true-by-omission nowhere:
// an unstamped browser is NOT in the funnel.
export const isInFunnelCohort = (): boolean => readCohort() === 'new';

// Not prefixed `orion-web:` — that prefix exists only so already-persisted keys keep
// working (see orion-web/CLAUDE.md); a NEW key is named for the product.
//
// ONE KEY PER MILESTONE rather than a single JSON object holding all three. Latching is
// a read-modify-write, and it is not atomic across tabs: with a shared object, two tabs
// latching different milestones close together would have the second write — built on a
// read taken before the first — silently drop the other's flag, and that milestone would
// fire a second time. Independent keys make the writes independent. The flat `'1'` value
// also removes a JSON.parse from `hasFired`, which is called on a hot path (see the
// workspace-milestone effect in MessagesPanel).
const keyFor = (milestone: Milestone): string => `symphony-gtm-funnel:${milestone}`;

export const hasFired = (milestone: Milestone): boolean => {
  if (typeof window === 'undefined') return false;
  try {
    return window.localStorage.getItem(keyFor(milestone)) === '1';
  } catch {
    // Private mode / blocked storage. Treat as "nothing fired yet". Note this no longer
    // means "the funnel repeats instead of going silent", which is what an earlier version
    // of this module optimised for: storage that can't be read can't hold the cohort stamp
    // either, so the gate in `fireOnce` stops these browsers first. That is the right way
    // round — un-latched, the workspace card replaying on every conversation load would
    // emit "Finish On boarding" on every page view.
    return false;
  }
};

// Fire `milestone` unless it already has been. Returns whether it fired, so a caller can
// chain the next step in the same pass.
const fireOnce = (milestone: Milestone, event: GtmEventName): boolean => {
  if (typeof window === 'undefined') return false;
  // Users who onboarded before this shipped are not in the funnel — see the cohort note
  // at the top. Checked here so all three milestones inherit it and the purchase events,
  // which call pushGtmEvent directly, do not.
  if (!isInFunnelCohort()) return false;
  if (hasFired(milestone)) return false;
  // Latch BEFORE pushing: a throw from the push must not leave the milestone unlatched,
  // or it retries on every subsequent render.
  try {
    window.localStorage.setItem(keyFor(milestone), '1');
  } catch {
    // Readable but unwritable — a quota trip after the cohort was already stamped. Push
    // anyway: a possibly-repeated event beats a missing one. (Storage that is dead in both
    // directions never reaches here; the cohort gate above catches it.)
  }
  pushGtmEvent(event);
  return true;
};

export const markOnboardingStarted = (): boolean => fireOnce('start', GtmEvent.startOnboarding);

export const markOnboardingFinished = (): boolean => fireOnce('finish', GtmEvent.finishOnboarding);

export const markActiveUser = (): boolean => fireOnce('active', GtmEvent.activeUser);

// A message the USER sent, from any send path. Which milestone it counts as depends on
// whether the workspace milestone has already landed: before it, the first send STARTS
// onboarding; after it, the first send is the ACTIVE-USER activation. Both are latched,
// so only the first send on each side of the workspace actually reports.
//
// This is called from `useChat`'s send/steer, right beside the AppsFlyer `logActiveUser`
// — NOT from the composer's submit handler. The composer is only one of four ways a user
// message is sent; the others (the home-launcher prefill autosend, a voice note, a
// choice-chip answer) never reach it, and a funnel that misses them under-counts its own
// first step.
export const reportFunnelSend = (): void => {
  if (hasFired('finish')) {
    markActiveUser();
  } else {
    markOnboardingStarted();
  }
};

// Test-only: clear the latch AND the cohort stamp. Exported rather than reaching into the
// keys from a test so the storage shape stays private to this module.
export const resetGtmFunnel = (): void => {
  if (typeof window === 'undefined') return;
  try {
    MILESTONES.forEach((milestone) => window.localStorage.removeItem(keyFor(milestone)));
    window.localStorage.removeItem(COHORT_KEY);
  } catch {
    /* nothing to reset */
  }
};
